import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';

interface Partner {
  id: string;
  name: string;
  logo_url?: string;
}

interface PartnerService {
  id: string;
  name: string;
  price: number;
}

interface Alteration {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  description: string;
  service_name: string;
  image_url: string;
  status: 'pending' | 'in_progress' | 'completed' | 'invoiced';
  price: number;
  created_at: string;
  partner_id: string;
  invoice_id?: string | null;
  partners: { name: string, logo_url?: string };
}

export default function AdminAlterations() {
  const [alterations, setAlterations] = useState<Alteration[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerServices, setPartnerServices] = useState<PartnerService[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'brand'>('date');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [editingId, setEditingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    partner_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    service_id: '',
    service_name: '',
    description: '',
    price: 0,
    image_url: ''
  });

  useEffect(() => {
    fetchAlterations();
    fetchPartners();
  }, []);

  const fetchAlterations = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('alterations')
      .select('*, partners(name, logo_url)')
      .order('created_at', { ascending: false })
      .order('id', { ascending: false });
    
    if (data) setAlterations(data);
    setLoading(false);
  };

  const fetchPartners = async () => {
    const { data } = await supabase.from('partners').select('id, name, logo_url').order('name');
    if (data) setPartners(data);
  };

  const fetchPartnerServices = async (partnerId: string) => {
    const { data } = await supabase.from('partner_services').select('*').eq('partner_id', partnerId).order('name');
    if (data) setPartnerServices(data);
  };

  const handlePartnerChange = (partnerId: string) => {
    setFormData({ ...formData, partner_id: partnerId, service_id: '', price: 0 });
    if (partnerId) fetchPartnerServices(partnerId);
  };

  const handleServiceChange = (serviceId: string) => {
    const service = partnerServices.find(s => s.id === serviceId);
    setFormData({ 
      ...formData, 
      service_id: serviceId,
      service_name: service?.name || (serviceId === 'manual' ? 'Servicio Manual' : formData.service_name),
      price: service?.price || formData.price 
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `alterations/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('alterations')
      .upload(filePath, file);

    if (!uploadError) {
      const { data } = supabase.storage.from('alterations').getPublicUrl(filePath);
      setFormData({ ...formData, image_url: data.publicUrl });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const service = partnerServices.find(s => s.id === formData.service_id);
    const finalServiceName = service?.name || formData.service_name || 'Servicio Manual';
    
    if (editingId) {
      const { error } = await supabase.from('alterations').update({
        partner_id: formData.partner_id,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        service_name: finalServiceName,
        price: formData.price,
        description: formData.description,
        image_url: formData.image_url,
      }).eq('id', editingId);

      if (!error) {
        closeForm();
        fetchAlterations();
      }
    } else {
      const { error } = await supabase.from('alterations').insert([{
        partner_id: formData.partner_id,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        service_name: finalServiceName,
        price: formData.price,
        description: formData.description,
        image_url: formData.image_url,
        status: 'pending'
      }]);

      if (!error) {
        closeForm();
        fetchAlterations();
      }
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ partner_id: '', client_name: '', client_email: '', client_phone: '', service_id: '', service_name: '', description: '', price: 0, image_url: '' });
  };

  const openEditForm = async (alt: Alteration) => {
    setFormData({
      partner_id: alt.partner_id,
      client_name: alt.client_name,
      client_email: alt.client_email,
      client_phone: alt.client_phone,
      service_id: 'manual', // Fallback, will try to match below
      service_name: alt.service_name,
      description: alt.description,
      price: alt.price,
      image_url: alt.image_url || ''
    });
    setEditingId(alt.id);
    setIsAdding(true);
    
    // Fetch services for this partner and try to match the service
    const { data: services } = await supabase.from('partner_services').select('*').eq('partner_id', alt.partner_id).order('name');
    if (services) {
      setPartnerServices(services);
      const matched = services.find(s => s.name === alt.service_name);
      if (matched) {
        setFormData(prev => ({ ...prev, service_id: matched.id }));
      }
    }
  };

  const sortedAlterations = [...alterations].sort((a, b) => {
    if (sortBy === 'date') {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    } else {
      const nameA = a.partners?.name || '';
      const nameB = b.partners?.name || '';
      const compare = nameA.localeCompare(nameB);
      return sortOrder === 'asc' ? compare : -compare;
    }
  });

  const updateStatus = async (id: string, status: Alteration['status']) => {
    // Optimistic update
    const previousAlterations = [...alterations];
    setAlterations(prev => prev.map(alt => alt.id === id ? { ...alt, status } : alt));

    const { error } = await supabase.from('alterations').update({ status }).eq('id', id);
    if (error) {
      console.error('Error updating status:', error);
      setAlterations(previousAlterations);
    } else {
      fetchAlterations();
    }
  };

  const updatePrice = async (id: string, price: number) => {
    // Optimistic update
    const previousAlterations = [...alterations];
    setAlterations(prev => prev.map(alt => alt.id === id ? { ...alt, price } : alt));

    const { error } = await supabase.from('alterations').update({ price }).eq('id', id);
    if (error) {
      console.error('Error updating price:', error);
      setAlterations(previousAlterations);
    } else {
      fetchAlterations();
    }
  };

  const deleteAlteration = async (id: string) => {
    const { error } = await supabase.from('alterations').delete().eq('id', id);
    if (!error) {
      setDeletingId(null);
      fetchAlterations();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-brand-black text-2xl">Gestión de Arreglos</h2>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Revisa las fotos y servicios solicitados por los clientes de partners.</p>
        </div>
        <button
          onClick={() => { closeForm(); setIsAdding(true); }}
          className="bg-brand-black text-white px-5 py-2.5 rounded-full text-sm font-sans flex items-center gap-2 hover:bg-brand-gray-800 transition-colors"
        >
          <i className="ri-add-line" />
          Nuevo Arreglo
        </button>
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="bg-brand-cream/30 p-8 rounded-3xl border border-brand-sage/30 overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h3 className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-400 font-bold">Asignación</h3>
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Marca / Partner</label>
                    <select 
                      required className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm"
                      value={formData.partner_id} onChange={(e) => handlePartnerChange(e.target.value)}
                    >
                      <option value="">Selecciona una marca...</option>
                      {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Servicio</label>
                    <select 
                      required className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm"
                      value={formData.service_id} onChange={(e) => handleServiceChange(e.target.value)}
                      disabled={!formData.partner_id}
                    >
                      <option value="">{formData.partner_id ? 'Selecciona servicio...' : 'Elige marca primero'}</option>
                      {partnerServices.map(s => <option key={s.id} value={s.id}>{s.name} ({s.price}€)</option>)}
                      <option value="manual">Otro (Precio manual)</option>
                    </select>
                  </div>
                  {formData.service_id === 'manual' && (
                    <div className="space-y-1.5">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1">Nombre del Servicio (Manual)</label>
                      <input 
                        type="text" required className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm"
                        value={formData.service_name} onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                        placeholder="Ej. Arreglo especial..."
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Precio Final (€)</label>
                    <input 
                      type="number" className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm"
                      value={formData.price} onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-400 font-bold">Datos del Cliente</h3>
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Nombre Completo</label>
                    <input type="text" required className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm" value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Teléfono</label>
                    <input type="tel" className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm" value={formData.client_phone} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Descripción</label>
                    <textarea rows={2} className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-2.5 text-sm resize-none" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-400 font-bold">Documentación</h3>
                  <div onClick={() => fileInputRef.current?.click()} className="w-full h-40 border-2 border-dashed border-brand-sage/30 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-brand-sage/5 transition-colors overflow-hidden relative bg-white">
                    {formData.image_url ? <img src={formData.image_url} alt="Prenda" className="w-full h-full object-cover" /> : <><i className="ri-camera-line text-2xl text-brand-sage mb-2" /><p className="text-[10px] text-brand-gray-400 font-sans uppercase tracking-widest text-center px-4">Subir foto de la prenda</p></>}
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-6 border-t border-brand-sage/20">
                <button type="button" onClick={closeForm} className="px-6 py-2.5 rounded-full text-sm font-sans text-brand-gray-500 hover:bg-brand-gray-100 transition-colors">Cancelar</button>
                <button type="submit" className="bg-brand-black text-white px-10 py-2.5 rounded-full text-sm font-sans hover:bg-brand-gray-800 transition-colors shadow-lg">
                  {editingId ? 'Guardar Cambios' : 'Crear Arreglo'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-3xl border border-brand-gray-100 overflow-x-auto shadow-sm [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="bg-brand-gray-50 border-b border-brand-gray-100">
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Cliente / Teléfono</th>
              <th 
                className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400 cursor-pointer hover:text-brand-black transition-colors"
                onClick={() => { if (sortBy === 'brand') setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); else { setSortBy('brand'); setSortOrder('asc'); } }}
              >
                <div className="flex items-center gap-1">
                  Marca / Partner
                  {sortBy === 'brand' && <i className={`ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`} />}
                </div>
              </th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Servicio / Descripción</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Precio (€)</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Estado</th>
              <th 
                className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400 text-right cursor-pointer hover:text-brand-black transition-colors"
                onClick={() => { if (sortBy === 'date') setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); else { setSortBy('date'); setSortOrder('desc'); } }}
              >
                <div className="flex items-center justify-end gap-1">
                  {sortBy === 'date' && <i className={`ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`} />}
                  Registro / Acciones
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-100">
            {sortedAlterations.map((alt) => (
              <tr key={alt.id} className="hover:bg-brand-gray-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-4">
                    <div onClick={() => alt.image_url && setSelectedImage(alt.image_url)} className={`w-14 h-14 rounded-xl bg-brand-gray-100 flex items-center justify-center overflow-hidden border border-brand-gray-200 ${alt.image_url ? 'cursor-pointer' : ''}`}>
                      {alt.image_url ? <img src={alt.image_url} alt="Prenda" className="w-full h-full object-cover" /> : <i className="ri-camera-off-line text-brand-gray-300" />}
                    </div>
                    <div>
                      <p className="font-sans text-brand-black font-medium text-sm">{alt.client_name}</p>
                      <p className="font-sans text-[10px] text-brand-gray-400 mt-0.5">{alt.client_phone || 'Sin Tel.'}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-brand-cream flex items-center justify-center overflow-hidden border border-brand-gray-100 flex-shrink-0">
                      {alt.partners?.logo_url ? <img src={alt.partners.logo_url} alt="" className="w-full h-full object-cover" /> : <i className="ri-building-line text-[10px] text-brand-sage" />}
                    </div>
                    <span className="font-sans text-[11px] uppercase tracking-wider text-brand-black font-bold">{alt.partners?.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 max-w-xs">
                  <div className="space-y-1">
                    <p className="font-sans text-brand-black font-semibold text-xs italic">{alt.service_name || 'Servicio General'}</p>
                    <p className="font-sans text-brand-gray-500 text-[11px] line-clamp-2">{alt.description}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <input type="number" disabled={alt.status === 'invoiced' && !!alt.invoice_id} className="w-20 bg-brand-gray-50 border border-brand-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-sage" value={alt.price} onChange={(e) => updatePrice(alt.id, Number(e.target.value))} />
                </td>
                <td className="px-6 py-4">
                  <select disabled={alt.status === 'invoiced' && !!alt.invoice_id} value={alt.status} onChange={(e) => updateStatus(alt.id, e.target.value as any)} className={`text-[10px] font-sans uppercase tracking-widest px-3 py-1.5 rounded-full border-none cursor-pointer ${alt.status === 'pending' ? 'bg-amber-100 text-amber-700' : alt.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : alt.status === 'completed' ? 'bg-brand-sage/20 text-brand-black' : 'bg-brand-gray-200 text-brand-gray-500'}`}>
                    <option value="pending">Pendiente</option>
                    <option value="in_progress">En Curso</option>
                    <option value="completed">Completado</option>
                    <option value="invoiced">Facturado</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {deletingId === alt.id ? (
                      <div className="flex items-center gap-1 bg-red-50 p-1 rounded-full border border-red-100 scale-90">
                        <button onClick={() => deleteAlteration(alt.id)} className="px-2 py-1 bg-red-600 text-white text-[9px] uppercase font-bold rounded-full hover:bg-red-700">Confirmar</button>
                        <button onClick={() => setDeletingId(null)} className="px-2 py-1 text-red-600 text-[9px] uppercase font-bold rounded-full hover:bg-red-100">No</button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditForm(alt)} className="p-2 hover:bg-blue-50 rounded-full text-brand-gray-400 hover:text-blue-600 transition-colors" title="Editar"><i className="ri-pencil-line" /></button>
                          <button onClick={() => window.open(`/receipt/${alt.id}`, '_blank')} className="p-2 hover:bg-brand-gray-50 rounded-full text-brand-gray-400 hover:text-brand-black transition-colors" title="Generar Recibo"><i className="ri-receipt-line" /></button>
                          <button onClick={() => setDeletingId(alt.id)} className="p-2 hover:bg-red-50 rounded-full text-brand-gray-300 hover:text-red-600 transition-colors" title="Eliminar Solicitud"><i className="ri-delete-bin-line" /></button>
                        </div>
                        <span className="text-brand-gray-300 text-[9px] font-sans">
                          {new Date(alt.created_at).toLocaleDateString()} {new Date(alt.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Lightbox para fotos */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedImage(null)} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-10 cursor-pointer">
            <img src={selectedImage} alt="Prenda ampliada" className="max-w-full max-h-full rounded-2xl shadow-2xl" />
            <button className="absolute top-10 right-10 text-white text-3xl"><i className="ri-close-line" /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
